// contexts/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { 
  getUserNotifications, 
  getUnreadCountNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '../services/api';
import { useNotificationWebSocket } from '../hooks/useNotificationWebSocket';
import { shouldSendNotification, showNotification, playNotificationSound } from '../utils/notificationUtils';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastNotification, setLastNotification] = useState(null);
  
  const notificationsRef = useRef(notifications);
  const unreadCountRef = useRef(unreadCount);
  
  // Update refs when state changes
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);
  
  useEffect(() => {
    unreadCountRef.current = unreadCount;
  }, [unreadCount]);
  
  // WebSocket connection for real-time notifications
  const { isConnected, subscribeToNotifications } = useNotificationWebSocket();
  
  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await getUnreadCountNotifications();
      if (response.success) {
        setUnreadCount(response.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user]);
  
  // Fetch notifications
  const fetchNotifications = useCallback(async (reset = false) => {
    if (!user?.user_id) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    
    if (reset) {
      setLoading(true);
      setPage(1);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const limit = 20;
      const offset = reset ? 0 : (page - 1) * limit;
      const response = await getUserNotifications(limit, offset);
      
      if (response.success) {
        const newNotifications = response.data || [];
        
        if (reset) {
          setNotifications(newNotifications);
          setHasMore(newNotifications.length === limit);
        } else {
          setNotifications(prev => [...prev, ...newNotifications]);
          setHasMore(newNotifications.length === limit);
        }
        
        if (response.unread_count !== undefined) {
          setUnreadCount(response.unread_count);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user, page]);
  
  // Handle new notification from WebSocket
  const handleNewNotification = useCallback(async (notification, newUnreadCount) => {
    console.log('🔔 New notification received:', notification.title);
    
    // Update notifications list
    setNotifications(prev => [notification, ...prev]);
    
    // Update unread count
    setUnreadCount(newUnreadCount);
    
    // Store last notification for potential use
    setLastNotification(notification);
    
    // Show browser notification if enabled
    if (shouldSendNotification(notification.type)) {
      showNotification(notification.title, {
        body: notification.message,
        type: notification.type,
        icon: '/favicon.ico',
        tag: notification.id?.toString(),
        requireInteraction: ['order', 'dispute', 'payment'].includes(notification.type),
        onClick: () => {
          // Navigate based on notification type
          handleNotificationClick(notification);
        }
      });
    }
    
    // Play sound if enabled
    const settings = JSON.parse(localStorage.getItem('notification_settings') || '{}');
    if (settings.sound !== false) {
      playNotificationSound(notification.type);
    }
    
  }, []);
  
  // Handle notification read from WebSocket
  const handleNotificationRead = useCallback((notificationId, newUnreadCount) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      )
    );
    setUnreadCount(newUnreadCount);
  }, []);
  
  // Handle unread count update
  const handleUnreadCountUpdate = useCallback((count) => {
    setUnreadCount(count);
  }, []);
  
  // Handle all notifications cleared
  const handleAllCleared = useCallback((newUnreadCount) => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, is_read: true }))
    );
    setUnreadCount(newUnreadCount);
  }, []);
  
  // Subscribe to WebSocket notifications
  useEffect(() => {
    if (!user?.user_id) return;
    
    const unsubscribe = subscribeToNotifications(
      handleNewNotification,
      handleNotificationRead,
      handleUnreadCountUpdate,
      handleAllCleared
    );
    
    return () => {
      unsubscribe();
    };
  }, [user, subscribeToNotifications, handleNewNotification, handleNotificationRead, handleUnreadCountUpdate, handleAllCleared]);
  
  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await markNotificationAsRead(notificationId);
      if (response.success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId ? { ...notif, is_read: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }, []);
  
  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await markAllNotificationsAsRead();
      if (response.success) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking all as read:', error);
      return false;
    }
  }, []);
  
  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const response = await deleteNotification(notificationId);
      if (response.success) {
        const deletedNotif = notificationsRef.current.find(n => n.id === notificationId);
        setNotifications(prev => 
          prev.filter(notif => notif.id !== notificationId)
        );
        if (deletedNotif && !deletedNotif.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }, []);
  
  // Load more notifications
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      setPage(prev => prev + 1);
      fetchNotifications(false);
    }
  }, [loadingMore, hasMore, fetchNotifications]);
  
  // Handle notification click (for navigation)
  const handleNotificationClick = useCallback((notification) => {
    // This will be implemented in the component using the context
    // We'll store the navigation function separately
  }, []);
  
  // Initial load
  useEffect(() => {
    if (user?.user_id) {
      fetchNotifications(true);
      fetchUnreadCount();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }
  }, [user, fetchNotifications, fetchUnreadCount]);
  
  // Refresh data when returning to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.user_id) {
        fetchUnreadCount();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchUnreadCount]);
  
  const value = {
    // State
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadingMore,
    isConnected,
    lastNotification,
    
    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMore,
    setNotifications,
    setUnreadCount,
    
    // WebSocket status
    wsConnected: isConnected
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;